import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  Projekt,
  ProjektRequest,
  ProjektZugriffBenutzer
} from '../../Models/projekt.model';

import { Customer } from '../../Models/customer.model';
import { Equipment } from '../../Models/equipment.model';

import { ProjektService } from '../../Services/projekt.service';
import { CustomerService } from '../../Services/customer.service';
import { EquipmentService } from '../../Services/equipment.service';
import { AuthService } from '../../api-authorization/authorize.service';
import { ProjektDataService } from '../../Services/offline/projekt-data.service';
import { ProjektOfflineService } from '../../Services/offline/projekt-offline.service';
import { Datenquelle, OfflineProjektStatus } from '../../Models/offline.model';
import { Subscription } from 'rxjs';


type ProjektStatusFilter =
  'alle'
  | 'nichtGestartet'
  | 'offen'
  | 'geprueft';


interface ProjektFilter {
  search: string;
  customer: string;
  status: ProjektStatusFilter;
}


/**
 * Projektübersicht:
 *
 * - Kachel- oder Listenansicht
 * - Volltextsuche
 * - Filter nach Kunde
 * - Filter nach Prüfstatus
 * - Formular zum Anlegen neuer Projekte
 *
 * Status:
 *
 * Nicht gestartet:
 * Keine Messstelle des Projekts hat bisher Prüfpunkte.
 *
 * Offen:
 * Mindestens eine Messstelle wurde gestartet, aber mindestens eine Messstelle
 * ist noch nicht gestartet oder mindestens ein Prüfpunkt ist noch offen.
 *
 * Geprüft:
 * Jede Messstelle aller Inbetriebnahmen hat Prüfpunkte und sämtliche
 * Prüfpunkte sind Erledigt oder Irrelevant.
 */
@Component({
  selector: 'app-projekte',
  templateUrl: './projekte.component.html',
  styleUrls: ['./projekte.component.css']
})
export class ProjekteComponent implements OnInit, OnDestroy {

  dataSource =
    new MatTableDataSource<Projekt>([]);


  displayedColumns = [
    'projektNummer',
    'name',
    'customerName',
    'equipmentName',
    'status'
  ];


  /**
   * Umschaltung zwischen Kachel-
   * und Listenansicht.
   */
  ansicht:
    'kacheln'
    | 'liste'
    = 'kacheln';


  kunden: Customer[] = [];
  anlagen: Equipment[] = [];


  isLoading = true;
  loadError = '';

  /**
   * Woher die Übersicht stammt. Ohne Verbindung sind nur die Projekte
   * dabei, die offline verfügbar gemacht wurden – sie lassen sich wie online
   * öffnen.
   */
  quelle: Datenquelle = 'server';
  standUtc: string | null = null;

  /** Offline-Zustand je Projekt – für das Kennzeichen auf Kachel und Zeile. */
  private offlineStatus = new Map<number, OfflineProjektStatus>();
  private offlineAbo?: Subscription;


  // =========================================================
  // FILTER
  // =========================================================

  /**
   * Freie Textsuche.
   */
  searchTerm = '';


  /**
   * Leerstring bedeutet:
   * alle Kunden.
   */
  selectedCustomer = '';


  /**
   * Statusfilter.
   */
  selectedStatus:
    ProjektStatusFilter
    = 'alle';


  // =========================================================
  // FORMULAR
  // =========================================================

  formOpen = false;

  saving = false;

  loadingAnlagen = false;

  formError = '';


  /** Projekte anlegen duerfen nur Admin und SuperAdmin. */
  canCreateProjekt = false;

  zugriffsBenutzer: ProjektZugriffBenutzer[] = [];

  loadingZugriffsBenutzer = false;

  zugriffsBenutzerError = '';


  form:
    ProjektRequest
    = this.leeresFormular();


  constructor(
    private service: ProjektService,
    private daten: ProjektDataService,
    private offline: ProjektOfflineService,
    private customerService: CustomerService,
    private equipmentService: EquipmentService,
    private router: Router,
    private snackBar: MatSnackBar,
    private authService: AuthService
  ) {
    this.offlineAbo = this.offline.offlineStatus$.subscribe(alle => {
      this.offlineStatus = new Map(alle.map(s => [s.projektId, s]));
    });
  }

  get istOffline(): boolean {
    return this.quelle === 'offline';
  }

  /** Offline-Zustand eines Projekts; null, wenn es nicht heruntergeladen ist. */
  offlineVon(projekt: Projekt): OfflineProjektStatus | null {
    const s = this.offlineStatus.get(projekt.id);
    return s && (s.verfuegbar || s.zustand === 'laedt') ? s : null;
  }

  offlineText(projekt: Projekt): string {
    const s = this.offlineVon(projekt);

    if (!s) {
      return '';
    }

    switch (s.zustand) {
      case 'laedt': return 'Wird heruntergeladen …';
      case 'ausstehend': return s.ausstehend === 1 ? '1 Änderung ausstehend' : `${s.ausstehend} Änderungen ausstehend`;
      case 'synchronisiert': return 'Wird synchronisiert …';
      case 'konflikt': return s.konflikte === 1 ? '1 Konflikt' : `${s.konflikte} Konflikte`;
      case 'fehler': return 'Synchronisierungsfehler';
      default: return 'Offline verfügbar';
    }
  }

  offlineIcon(projekt: Projekt): string {
    switch (this.offlineVon(projekt)?.zustand) {
      case 'laedt': return 'cloud_download';
      case 'ausstehend': return 'cloud_queue';
      case 'synchronisiert': return 'sync';
      case 'konflikt': return 'merge_type';
      case 'fehler': return 'sync_problem';
      default: return 'offline_pin';
    }
  }

  offlineKlasse(projekt: Projekt): string {
    switch (this.offlineVon(projekt)?.zustand) {
      case 'laedt':
      case 'synchronisiert': return 'status-badge--info';
      case 'ausstehend': return 'status-badge--warning';
      case 'konflikt':
      case 'fehler': return 'status-badge--error';
      default: return 'status-badge--success';
    }
  }


  // =========================================================
  // INITIALISIERUNG
  // =========================================================

  ngOnDestroy(): void {
    this.offlineAbo?.unsubscribe();
  }


  ngOnInit(): void {

    this.canCreateProjekt =
      this.authService.canCreateProjekt();


    /*
     * MatTableDataSource bekommt nur einen String als Filter.
     *
     * Deshalb serialisieren wir unsere verschiedenen
     * Filterwerte als JSON.
     */
    this.dataSource.filterPredicate =
      (
        projekt: Projekt,
        filterString: string
      ): boolean => {

        let filter: ProjektFilter;


        try {

          filter =
            JSON.parse(
              filterString
            ) as ProjektFilter;

        } catch {

          /*
           * Solange noch kein Filter gesetzt wurde,
           * werden alle Projekte angezeigt.
           */
          return true;

        }


        // =====================================================
        // 1. TEXTSUCHE
        // =====================================================

        const suchfelder = [
          projekt.projektNummer,
          projekt.name,
          projekt.customerName,
          projekt.equipmentName
        ];


        const passtZurSuche =
          !filter.search
          ||
          suchfelder.some(
            feld =>
              (feld ?? '')
                .toLowerCase()
                .includes(
                  filter.search
                )
          );


        // =====================================================
        // 2. KUNDE
        // =====================================================

        const passtZumKunden =
          !filter.customer
          ||
          (
            projekt.customerName
            ?? ''
          ) === filter.customer;


        // =====================================================
        // 3. STATUS
        // =====================================================

        let passtZumStatus =
          true;


        if (
          filter.status ===
          'nichtGestartet'
        ) {

          passtZumStatus =
            this.istNichtGestartet(
              projekt
            );

        }


        if (
          filter.status ===
          'offen'
        ) {

          passtZumStatus =
            this.istOffen(
              projekt
            );

        }


        if (
          filter.status ===
          'geprueft'
        ) {

          passtZumStatus =
            this.istFertig(
              projekt
            );

        }


        return (
          passtZurSuche
          &&
          passtZumKunden
          &&
          passtZumStatus
        );
      };


    // =========================================================
    // KUNDEN LADEN
    // =========================================================

    this.customerService
      .getCustomers()
      .subscribe({

        next: kunden => {

          this.kunden =
            kunden ?? [];

        },

        error: () => {

          this.kunden = [];

        }

      });


    // =========================================================
    // PROJEKTE LADEN
    // =========================================================

    this.ladeProjekte();
  }


  // =========================================================
  // PROJEKTE LADEN
  // =========================================================

  private ladeProjekte(): void {

    this.isLoading = true;
    this.loadError = '';


    // Online vom Server, ohne Verbindung die heruntergeladenen Projekte.
    this.daten
      .getProjekte()
      .subscribe({

        next: ergebnis => {

          this.dataSource.data =
            ergebnis.daten ?? [];

          this.quelle = ergebnis.quelle;
          this.standUtc = ergebnis.standUtc ?? null;

          this.isLoading =
            false;

          if (ergebnis.quelle === 'offline' && this.dataSource.data.length === 0) {
            this.loadError =
              'Keine Verbindung zum Server. Angezeigt werden nur Projekte, '
              + 'die vorher offline verfügbar gemacht wurden – derzeit keines.';
          }


          /*
           * Falls später einmal Filterzustände beim
           * erneuten Laden bestehen bleiben, werden
           * sie hier erneut angewendet.
           */
          this.applyFilter();
        },


        error: () => {

          this.loadError =
            'Die Projekte konnten nicht geladen werden.';

          this.isLoading =
            false;
        }

      });
  }


  // =========================================================
  // FILTER
  // =========================================================

  applyFilter(): void {

    const filter:
      ProjektFilter = {

      search:
        this.searchTerm
          .trim()
          .toLowerCase(),

      customer:
        this.selectedCustomer,

      status:
        this.selectedStatus
    };


    this.dataSource.filter =
      JSON.stringify(
        filter
      );
  }


  /**
   * Setzt Suche, Kunde und Status gleichzeitig zurück.
   */
  clearFilter(): void {

    this.searchTerm = '';

    this.selectedCustomer = '';

    this.selectedStatus =
      'alle';


    this.applyFilter();
  }


  // =========================================================
  // NAVIGATION
  // =========================================================

  oeffnen(
    projekt: Projekt
  ): void {

    this.router.navigate([
      '/projekte',
      projekt.id
    ]);
  }


  // =========================================================
  // STATUS
  // =========================================================

  /**
   * Der Fortschritt kommt bereits korrekt vom Backend.
   * Jede Messstelle zählt gleich stark; eine Messstelle ohne Prüfpunkte
   * trägt 0 % bei.
   */
  fortschritt(
    projekt: Projekt
  ): number {

    return projekt.fortschrittProzent ?? 0;
  }


  /**
   * Nicht gestartet: Noch keine Messstelle hat Prüfpunkte.
   * Das gilt auch für Projekte ohne Inbetriebnahme.
   */
  istNichtGestartet(
    projekt: Projekt
  ): boolean {

    return (projekt.messstellenGestartet ?? 0) === 0;
  }


  /**
   * Geprüft: Jede vorhandene Messstelle wurde gestartet und vollständig
   * abgearbeitet. Messstellen ohne Prüfpunkte verhindern den Abschluss.
   */
  istFertig(
    projekt: Projekt
  ): boolean {

    const gesamt = projekt.messstellenGesamt ?? 0;
    const abgeschlossen = projekt.messstellenAbgeschlossen ?? 0;

    return gesamt > 0 && abgeschlossen >= gesamt;
  }


  /**
   * Offen: Mindestens eine Messstelle wurde gestartet, aber das Projekt
   * ist noch nicht vollständig geprüft.
   */
  istOffen(
    projekt: Projekt
  ): boolean {

    return !this.istNichtGestartet(projekt)
      && !this.istFertig(projekt);
  }


  statusText(
    projekt: Projekt
  ): string {

    if (this.istFertig(projekt)) {
      return 'Geprüft';
    }

    if (this.istNichtGestartet(projekt)) {
      return 'Nicht gestartet';
    }

    return 'Offen';
  }


  // =========================================================
  // PROJEKT ANLEGEN
  // =========================================================

  neuesProjekt(): void {

    if (!this.canCreateProjekt) {
      return;
    }


    this.form =
      this.leeresFormular();

    this.anlagen = [];

    this.formError = '';

    this.zugriffsBenutzerError = '';

    this.formOpen = true;


    this.ladeZugriffsBenutzer();
  }

  private ladeZugriffsBenutzer(): void {

    this.loadingZugriffsBenutzer = true;

    this.zugriffsBenutzerError = '';


    this.service
      .getZugriffsBenutzer()
      .subscribe({

        next: benutzer => {

          this.zugriffsBenutzer =
            benutzer ?? [];

          this.loadingZugriffsBenutzer =
            false;
        },


        error: () => {

          this.zugriffsBenutzer = [];

          this.loadingZugriffsBenutzer =
            false;

          this.zugriffsBenutzerError =
            'Die Benutzer konnten nicht geladen werden.';
        }

      });
  }


  abbrechen(): void {

    this.formOpen = false;

    this.formError = '';

    this.form =
      this.leeresFormular();

    this.anlagen = [];
  }


  // =========================================================
  // KUNDE / ANLAGE
  // =========================================================

  onKundeChange(
    customerId: number | null
  ): void {

    this.form.customerId =
      customerId ?? null;


    /*
     * Eine Anlage des alten Kunden darf
     * nicht stehen bleiben.
     */
    this.form.equipmentId =
      null;


    this.anlagen = [];


    if (customerId) {

      this.ladeAnlagen(
        customerId
      );

    }
  }


  private ladeAnlagen(
    customerId: number
  ): void {

    this.loadingAnlagen =
      true;


    this.equipmentService
      .getEquipmentsByCustomer(
        customerId
      )
      .subscribe({

        next: anlagen => {

          this.anlagen =
            anlagen ?? [];

          this.loadingAnlagen =
            false;
        },


        error: () => {

          this.anlagen = [];

          this.loadingAnlagen =
            false;
        }

      });
  }


  // =========================================================
  // FORMULAR VALIDIEREN
  // =========================================================

  /*
   * Der Kunde wird bewusst NICHT geprüft: ein Projekt darf ohne Kunden
   * entstehen – zu Projektbeginn steht oft noch nicht fest, für wen
   * gearbeitet wird. Pflicht bleiben Name und Projektnummer, denn ohne
   * sie ist das Projekt nicht auffindbar.
   */
  get istGueltig(): boolean {

    return (
      this.form.name
        .trim()
        .length > 0

      &&

      this.form.projektNummer
        .trim()
        .length > 0
    );
  }


  // =========================================================
  // SPEICHERN
  // =========================================================

  speichern(): void {

    if (
      this.saving
      ||
      !this.istGueltig
    ) {

      return;

    }


    this.saving = true;

    this.formError = '';


    const request:
      ProjektRequest = {

      name:
        this.form.name.trim(),

      projektNummer:
        this.form.projektNummer.trim(),

      customerId:
        this.form.customerId,

      equipmentId:
        this.form.equipmentId
        ?? null,

      userIds:
        this.form.userIds ?? []
    };


    this.service
      .createProjekt(
        request
      )
      .subscribe({

        next: projekt => {

          this.saving =
            false;


          this.abbrechen();


          this.snackBar.open(
            'Projekt angelegt.',
            'OK',
            {
              duration: 2500
            }
          );


          /*
           * Direkt ins Detail.
           * Dort werden die Inbetriebnahmen angelegt.
           */
          this.router.navigate([
            '/projekte',
            projekt.id
          ]);
        },


        error: err => {

          this.saving =
            false;


          /*
           * Fachliche Serverfehler bevorzugt anzeigen,
           * z.B. doppelte Projektnummer.
           */
          this.formError =
            err?.error?.message

            ?? (
              err?.status === 403

                ? 'Für das Anlegen von Projekten fehlt die Berechtigung.'

                : 'Das Projekt konnte nicht gespeichert werden.'
            );
        }

      });
  }


  // =========================================================
  // HILFSMETHODEN
  // =========================================================

  private leeresFormular():
    ProjektRequest {

    return {
      name: '',
      projektNummer: '',
      customerId: null,
      equipmentId: null,
      userIds: []
    };
  }


  trackByProjekt(
    _index: number,
    projekt: Projekt
  ): number {

    return projekt.id;
  }
}